import {
  RestaurantSettingKey,
  DEFAULT_RECOMMENDED_TIP_SETTING,
  DEFAULT_GIFT_CARD_AMOUNTS,
  type RestaurantSetting,
} from "@lemmonhq/types";
import {
  parseJsonValue,
  getSettingValue,
  createSetting,
  getBooleanSetting,
} from "@app/data/helpers/settings_helpers";
import { parseMobilePosOrderTypes } from "../../../../shared/mobilePosOrderTypes";
import type { ConfigSettingsFormState } from "./types";

const getSetting = (settings: RestaurantSetting[], key: string): any => {
  return getSettingValue(settings, key);
};

export const createDefaultConfigSettingsForm = (): ConfigSettingsFormState => ({
  start_time: "09:00",
  end_time: "22:00",
  take_away: "no",
  delivery: "no",
  vacation_schedule: { items: [] },
  notification_settings: { items: [] },
  reduce_quantities_update_kitchen_default: false,
  allow_orders_exceeding_table_credit: false,
  gift_card_amounts: DEFAULT_GIFT_CARD_AMOUNTS,
  mobile_pos_order_types: [],
  recommended_tips: null,
});

export const parseSettings = (
  settings: RestaurantSetting[],
): ConfigSettingsFormState => {
  return {
    start_time:
      (getSetting(settings, RestaurantSettingKey.START_TIME) as string) ||
      "09:00",
    end_time:
      (getSetting(settings, RestaurantSettingKey.END_TIME) as string) ||
      "22:00",
    take_away:
      (getSetting(settings, RestaurantSettingKey.TAKE_AWAY) as "yes" | "no") ||
      "no",
    delivery:
      (getSetting(settings, RestaurantSettingKey.DELIVERY) as "yes" | "no") ||
      "no",
    vacation_schedule: parseJsonValue(
      getSetting(settings, RestaurantSettingKey.VACATION_SCHEDULE),
      { items: [] },
    ),
    notification_settings: parseJsonValue(
      getSetting(settings, RestaurantSettingKey.NOTIFICATION_SETTINGS),
      { items: [] },
    ),
    reduce_quantities_update_kitchen_default: getBooleanSetting(
      settings,
      RestaurantSettingKey.REDUCE_QUANTITIES_UPDATE_KITCHEN_DEFAULT,
      false,
    ),
    allow_orders_exceeding_table_credit: getBooleanSetting(
      settings,
      RestaurantSettingKey.ALLOW_ORDERS_EXCEEDING_TABLE_CREDIT,
      false,
    ),
    gift_card_amounts: parseJsonValue(
      getSetting(settings, RestaurantSettingKey.GIFT_CARD_AMOUNTS),
      DEFAULT_GIFT_CARD_AMOUNTS,
    ),
    mobile_pos_order_types: parseMobilePosOrderTypes(
      getSetting(settings, RestaurantSettingKey.MOBILE_POS_ORDER_TYPES),
    ),
    recommended_tips: parseJsonValue(
      getSetting(settings, RestaurantSettingKey.RECOMMENDED_TIPS),
      null,
    ),
  };
};

export const serializeConfig = (config: ConfigSettingsFormState) =>
  JSON.stringify(config);

export const createSettingsPayload = (config: ConfigSettingsFormState) => [
  createSetting(
    RestaurantSettingKey.START_TIME,
    config.start_time || "09:00",
    "string",
  ),
  createSetting(
    RestaurantSettingKey.END_TIME,
    config.end_time || "22:00",
    "string",
  ),
  createSetting(RestaurantSettingKey.TAKE_AWAY, config.take_away, "string"),
  createSetting(RestaurantSettingKey.DELIVERY, config.delivery, "string"),
  createSetting(
    RestaurantSettingKey.VACATION_SCHEDULE,
    config.vacation_schedule,
    "json",
  ),
  createSetting(
    RestaurantSettingKey.NOTIFICATION_SETTINGS,
    config.notification_settings,
    "json",
  ),
  createSetting(
    RestaurantSettingKey.REDUCE_QUANTITIES_UPDATE_KITCHEN_DEFAULT,
    config.reduce_quantities_update_kitchen_default,
    "boolean",
  ),
  createSetting(
    RestaurantSettingKey.ALLOW_ORDERS_EXCEEDING_TABLE_CREDIT,
    config.allow_orders_exceeding_table_credit,
    "boolean",
  ),
  createSetting(
    RestaurantSettingKey.GIFT_CARD_AMOUNTS,
    config.gift_card_amounts,
    "json",
  ),
  createSetting(
    RestaurantSettingKey.MOBILE_POS_ORDER_TYPES,
    config.mobile_pos_order_types,
    "json",
  ),
  ...(config.recommended_tips != null
    ? [createSetting(RestaurantSettingKey.RECOMMENDED_TIPS, config.recommended_tips, "json")]
    : []),
];
